'use client';

import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { formatSlotTime, formatTimezoneLabel } from './booking-utils';

const DAY_CHIP_SX = {
  flex: '0 0 auto',
  minWidth: 108,
  borderRadius: 0,
  border: '1px solid rgba(0,0,0,0.18)',
  bgcolor: 'transparent',
  color: '#0a0a0a',
  textTransform: 'none',
  px: 1.5,
  py: 1.25,
  lineHeight: 1.25,
  '&:hover': {
    borderColor: '#0a0a0a',
    bgcolor: 'rgba(0,0,0,0.04)',
  },
};

const DAY_CHIP_SELECTED_SX = {
  ...DAY_CHIP_SX,
  bgcolor: '#0a0a0a',
  color: '#fff',
  borderColor: '#0a0a0a',
  '&:hover': {
    bgcolor: '#0a0a0a',
    color: '#fff',
    borderColor: '#0a0a0a',
  },
};

const SLOT_BTN_SX = {
  borderRadius: 0,
  border: '1px solid rgba(0,0,0,0.18)',
  bgcolor: 'transparent',
  color: '#0a0a0a',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 96,
  '&:hover': {
    borderColor: '#0a0a0a',
    bgcolor: 'rgba(0,0,0,0.04)',
  },
};

const SLOT_BTN_SELECTED_SX = {
  ...SLOT_BTN_SX,
  bgcolor: '#0a0a0a',
  color: '#fff',
  borderColor: '#0a0a0a',
  '&:hover': {
    bgcolor: '#0a0a0a',
    color: '#fff',
  },
};

export default function BookingAvailabilityGrid({
  days,
  timezone,
  selectedDate,
  selectedStartsAt,
  loading,
  error,
  onSelectDate,
  onSelectSlot,
  emptyMessage = 'No available times in this range. Try another designer or check back later.',
}) {
  const activeDay = days.find(d => d.date === selectedDate) || days[0] || null;
  const slots = activeDay?.slots || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} sx={{ color: '#0a0a0a' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!days.length) {
    return <Typography sx={{ py: 2, color: 'text.secondary' }}>{emptyMessage}</Typography>;
  }

  return (
    <Stack spacing={2.5}>
      {timezone ? (
        <Typography
          sx={{
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          Times shown in {formatTimezoneLabel(timezone)}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1,
          WebkitOverflowScrolling: 'touch',
        }}
        role="listbox"
        aria-label="Available days"
      >
        {days.map(day => {
          const selected = (selectedDate || activeDay?.date) === day.date;
          const shortLabel = day.label?.split(',')[0] || day.date;
          const datePart = day.date.slice(5).replace('-', '/');
          return (
            <Button
              key={day.date}
              role="option"
              aria-selected={selected}
              variant="outlined"
              onClick={() => onSelectDate(day.date)}
              sx={selected ? DAY_CHIP_SELECTED_SX : DAY_CHIP_SX}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  component="span"
                  sx={{ display: 'block', fontSize: 12, fontWeight: 600 }}
                >
                  {shortLabel}
                </Typography>
                <Typography component="span" sx={{ display: 'block', fontSize: 11, opacity: 0.8 }}>
                  {datePart}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
        role="listbox"
        aria-label="Available times"
      >
        {slots.map(slot => {
          const selected = selectedStartsAt === slot.startsAt;
          return (
            <Button
              key={slot.startsAt}
              role="option"
              aria-selected={selected}
              variant="outlined"
              onClick={() => onSelectSlot(slot)}
              sx={selected ? SLOT_BTN_SELECTED_SX : SLOT_BTN_SX}
            >
              {formatSlotTime(slot.startsAt, timezone)}
            </Button>
          );
        })}
      </Box>
    </Stack>
  );
}

BookingAvailabilityGrid.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      label: PropTypes.string,
      slots: PropTypes.arrayOf(
        PropTypes.shape({
          startsAt: PropTypes.string.isRequired,
          endsAt: PropTypes.string,
        }),
      ),
    }),
  ),
  timezone: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedStartsAt: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onSelectDate: PropTypes.func.isRequired,
  onSelectSlot: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
};

BookingAvailabilityGrid.defaultProps = {
  days: [],
  timezone: '',
  selectedDate: '',
  selectedStartsAt: '',
  loading: false,
  error: '',
  emptyMessage: undefined,
};
